// @ts-strict-ignore
import { DefinedMetaElementInfoPropertyDef } from '@project/constants/defined-meta-element-info-property-def';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { DefinedMetaElementPropertyVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { CustomArticleInfoVO } from '@project/components/article/model/vo/custom-article.vo';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { PropertyUtils } from './property-utils';
import { StringUtils } from '@shared/util/string-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { ArrayUtils } from '@shared/util/array-utils';

export class CustomArticleUtils {
  public static getCustomArticleNumber(customArticleUsage: ArticleUsageVO): string {
    return ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(customArticleUsage, ArticleUsageInfoPropertyDef.CUSTOM_ARTICLE_NUMBER);
  }

  public static getCustomImageUserId(customArticleUsage: ArticleUsageVO): number {
    return ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(customArticleUsage, ArticleUsageInfoPropertyDef.CUSTOM_IMAGE_USER_ID);
  }

  public static applyImageHashToArticleConnection(articleUsage: ArticleUsageVO, customArticle: ArticleVO, imageHash: string): void {
    if (!articleUsage || !imageHash) {
      return;
    }
    const imageConnection = this.getCustomImageArea(customArticle);
    if (!imageConnection) {
      return;
    }
    if (!articleUsage.definedMetaElementInfoProperties) {
      articleUsage.definedMetaElementInfoProperties = new Array<DefinedMetaElementPropertyVO>();
    }
    ArticleUsageUtils.setDefinedMetaElementInfoPropertyStringValue(
      articleUsage,
      imageConnection.name,
      DefinedMetaElementInfoPropertyDef.CUSTOM_IMAGE_HASH,
      imageHash
    );
  }

  public static getCustomImageArea(customArticle: ArticleVO): TextureAreaVO {
    if (!customArticle) {
      return null;
    }
    return ArticleUtils.findFirstTextureArea(customArticle.metaElements, [TextureAreaVO.TYPE_IMAGE]);
  }

  public static getCustomArticleWidth(customArticleUsage: ArticleUsageVO): number {
    return ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(customArticleUsage, ArticleUsageInfoPropertyDef.ARTICLE_LENGTH);
  }

  public static getCustomArticleHeight(customArticleUsage: ArticleUsageVO): number {
    return ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(customArticleUsage, ArticleUsageInfoPropertyDef.ARTICLE_HEIGHT);
  }

  public static getCustomArticleDepth(customArticleUsage: ArticleUsageVO): number {
    return ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(customArticleUsage, ArticleUsageInfoPropertyDef.ARTICLE_DEPTH);
  }

  public static getCustomArticleDepthOffset(customArticleUsage: ArticleUsageVO): number {
    return ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(customArticleUsage, ArticleUsageInfoPropertyDef.ARTICLE_DEPTH_OFFSET);
  }

  public static applyCustomArticleInfoToArticleUsage(articleUsage: ArticleUsageVO, customArticleInfo: CustomArticleInfoVO): void {
    if (!articleUsage || !customArticleInfo) {
      return;
    }
    if (!articleUsage.infoProperties) {
      articleUsage.infoProperties = [];
    }
    const customArticleProperties: PropertyVO[] = this.convertCustomArticleInfoToProperties(customArticleInfo);
    for (const customProperty of customArticleProperties) {
      PropertyUtils.setPropertyValues(articleUsage.infoProperties, customProperty.name, customProperty.values);
    }
  }

  public static convertCustomArticleInfoToProperties(customArticleInfo: CustomArticleInfoVO): PropertyVO[] {
    if (!customArticleInfo) {
      return [];
    }
    const properties: PropertyVO[] = [];
    PropertyUtils.setFloatPropertyValue(properties, ArticleUsageInfoPropertyDef.ARTICLE_LENGTH, customArticleInfo.articleWidth);
    PropertyUtils.setFloatPropertyValue(properties, ArticleUsageInfoPropertyDef.ARTICLE_HEIGHT, customArticleInfo.articleHeight);
    PropertyUtils.setFloatPropertyValue(properties, ArticleUsageInfoPropertyDef.ARTICLE_DEPTH, customArticleInfo.articleDepth);
    PropertyUtils.setFloatPropertyValue(properties, ArticleUsageInfoPropertyDef.ARTICLE_DEPTH_OFFSET, customArticleInfo.articleDepthOffset);
    PropertyUtils.setStringPropertyValue(properties, ArticleUsageInfoPropertyDef.CUSTOM_ARTICLE_NUMBER, customArticleInfo.articleNumber);
    if (!StringUtils.isEmpty(customArticleInfo.articleDescription)) {
      PropertyUtils.setStringPropertyValue(properties, ArticleUsageInfoPropertyDef.CUSTOM_ARTICLE_DESCRIPTION, customArticleInfo.articleDescription);
    }
    if (NumberUtils.isNumber(customArticleInfo.userId)) {
      PropertyUtils.setIntPropertyValue(properties, ArticleUsageInfoPropertyDef.CUSTOM_IMAGE_USER_ID, customArticleInfo.userId);
    }
    if (!StringUtils.isEmpty(customArticleInfo.imageHash)) {
      PropertyUtils.setStringPropertyValue(properties, ArticleUsageInfoPropertyDef.CUSTOM_ARTICLE_IMAGE_HASH, customArticleInfo.imageHash);
    }
    return properties;
  }

  public static findCustomArticleInfoById(articleInfoId: number, customArticleInfos: CustomArticleInfoVO[]): CustomArticleInfoVO {
    if (NumberUtils.isNotNumber(articleInfoId) || ArrayUtils.isEmpty(customArticleInfos)) {
      return null;
    }
    for (const articleInfo of customArticleInfos) {
      if (articleInfo.id === articleInfoId) {
        return articleInfo;
      }
    }
    return null;
  }

  public static convertUsagePropertiesToCustomArticleInfo(customArticleUsage: ArticleUsageVO): CustomArticleInfoVO {
    if (customArticleUsage == null) {
      return null;
    }
    const customInfo: CustomArticleInfoVO = new CustomArticleInfoVO();
    customInfo.articleWidth = this.getCustomArticleWidth(customArticleUsage);
    customInfo.articleHeight = this.getCustomArticleHeight(customArticleUsage);
    customInfo.articleDepth = this.getCustomArticleDepth(customArticleUsage);
    customInfo.articleDepthOffset = this.getCustomArticleDepthOffset(customArticleUsage);
    customInfo.articleNumber = this.getCustomArticleNumber(customArticleUsage);
    customInfo.articleDescription = this.getCustomArticleDescription(customArticleUsage);
    customInfo.userId = this.getCustomImageUserId(customArticleUsage);
    return customInfo;
  }

  public static getCustomArticleDescription(customArticleUsage: ArticleUsageVO): string {
    return ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(customArticleUsage, ArticleUsageInfoPropertyDef.CUSTOM_ARTICLE_DESCRIPTION);
  }
}
